import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { StatusOption, UserGetDTO, UserStatus } from '../../interfaces/users-interface';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Select } from 'primeng/select';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'lib-user-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    ProgressSpinner,
    TranslatePipe,
    Select,
    FormsModule,
  ],
  templateUrl: './user-table.component.html',
  styleUrl: './user-table.component.css',
})
export class UsersTableComponent {
  @Input() users: UserGetDTO[] = [];
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Input() first = 0;
  @Input() rows = 10;
  @Input() selectedStatus: UserStatus = 'all';
  @Input() statusOptions: StatusOption[] = [];
  @Input() searchTerm = '';

  @Output() pageChange = new EventEmitter<{
    page: number;
    rows: number;
  }>();
  @Output() statusFilterChange = new EventEmitter<UserStatus>();
  @Output() searchTermChange = new EventEmitter<string>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() createUserClick = new EventEmitter<void>();
  @Output() editUser = new EventEmitter<UserGetDTO>();
  @Output() deleteUser = new EventEmitter<UserGetDTO>();
  @Output() toggleStatus = new EventEmitter<{ user: UserGetDTO; status: boolean }>();

  onEditUser(user: UserGetDTO) {
    this.editUser.emit(user);
  }

  onDeleteUser(user: UserGetDTO) {
    this.deleteUser.emit(user);
  }

  onToggleStatus(user: UserGetDTO) {
    this.toggleStatus.emit({ user, status: !user.is_active });
  }

  onPageChange(event: TableLazyLoadEvent) {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.rows;
    const page = first / rows + 1;
    this.pageChange.emit({ page, rows });
  }

  onStatusChange(value: UserStatus): void {
    this.statusFilterChange.emit(value);
  }

  onSearchInput(value: string): void {
    this.searchTermChange.emit(value);
  }

  onSearchClear(): void {
    this.searchTermChange.emit('');
  }

  onClearFilters(): void {
    this.clearFilters.emit();
  }

  onCreateUser(): void {
    this.createUserClick.emit();
  }

  hasActiveFilters(): boolean {
    return this.selectedStatus !== 'all' || this.searchTerm.trim().length > 0;
  }

  getRoleNames(role: unknown): string[] {
    if (!role) return [];

    if (Array.isArray(role)) {
      return role
        .map((item) => {
          if (typeof item === 'string') return item;
          if (typeof item === 'object' && item && 'name' in (item as Record<string, unknown>)) {
            return String((item as Record<string, unknown>)['name'] ?? '').trim();
          }
          return '';
        })
        .filter(Boolean);
    }

    if (typeof role === 'string') return [role];

    if (typeof role === 'object' && role && 'name' in (role as Record<string, unknown>)) {
      const roleName = String((role as Record<string, unknown>)['name'] ?? '').trim();
      return roleName ? [roleName] : [];
    }

    return [];
  }

  getVisibleRoles(role: unknown): string[] {
    return this.getRoleNames(role).slice(0, 2);
  }

  getHiddenRolesCount(role: unknown): number {
    return Math.max(this.getRoleNames(role).length - 2, 0);
  }

  getHiddenRolesText(role: unknown): string {
    return this.getRoleNames(role).slice(2).join(', ');
  }

  getInitials(name: string | null | undefined): string {
    const value = String(name ?? '').trim();
    if (!value) return 'SN';

    const parts = value.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }

  getAvatarColorClass(name: string | null | undefined): string {
    const palette = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-violet-100 text-violet-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const value = String(name ?? '');
    const hash = value.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return palette[hash % palette.length] ?? palette[0];
  }

  getRowMenuItems(user: UserGetDTO): MenuItem[] {
    return [
      {
        label: 'Editar usuario',
        icon: 'pi pi-pencil',
        command: () => this.onEditUser(user),
      },
      {
        label: user.is_active ? 'Deshabilitar' : 'Habilitar',
        icon: user.is_active ? 'pi pi-ban' : 'pi pi-check',
        command: () => this.onToggleStatus(user),
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        styleClass: 'text-red-600 dark:text-red-400',
        command: () => this.onDeleteUser(user),
      },
    ];
  }
}
